import type React from "react";
import { useState } from "react";
import {
  Box, Chip, IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, People } from "@mui/icons-material";
import type {
  PharmaEligibleLocation, PharmaEnrolledLocation, PharmaStudyRollup,
  PharmaStudySiteStatus,
} from "@/api/types";
import { brand } from "@/theme/brandColors";
import { formatStatusLine } from "./LocationsInDAPanel";
import { LocationPersonnelTable } from "./LocationPersonnelTable";
import { MilestoneBar } from "./MilestoneBar";

const STATUS_COLOR: Record<PharmaStudySiteStatus, "success" | "warning" | "default"> = {
  active: "success",
  pending: "warning",
  cancelled: "default",
};

interface Props {
  rollup: PharmaStudyRollup;
  onEnroll: (locationId: string) => void;
  onUnenroll: (locationId: string) => void;
}

export function PharmaStudyCard({ rollup, onEnroll, onUnenroll }: Props) {
  const { study, site_status, enrolled_locations, eligible_locations, milestones } = rollup;
  // Per-participation expanded state for the personnel section.
  const [openPersonnel, setOpenPersonnel] = useState<Set<string>>(new Set());
  const togglePersonnel = (participationId: string) =>
    setOpenPersonnel((s) => {
      const n = new Set(s);
      if (n.has(participationId)) n.delete(participationId);
      else n.add(participationId);
      return n;
    });
  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", "&:last-child": { borderBottom: 0 } }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontWeight: 600 }}>{study.sponsor ?? "—"}</Typography>
        <Typography sx={{ fontFamily: "ui-monospace, Menlo, monospace", color: "text.secondary", fontSize: 13 }}>
          {study.protocol_number ?? study.code}
        </Typography>
        {study.nct_id && (
          <Typography sx={{ fontFamily: "ui-monospace, Menlo, monospace", color: "primary.main", fontSize: 12 }}>
            {study.nct_id}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Chip size="small" label={site_status} color={STATUS_COLOR[site_status]} />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {study.phase ? `${study.phase} · ` : ""}{study.study_type} · Study status: {study.status}
      </Typography>

      {milestones.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <MilestoneBar milestones={milestones} />
        </Box>
      )}

      {enrolled_locations.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", fontStyle: "italic", mb: 1 }}
        >
          No locations enrolled yet — pick one from the list below to
          enroll the first location in this study.
        </Typography>
      )}

      <Stack spacing={1}>
        {enrolled_locations.map((loc: PharmaEnrolledLocation) => {
          const open = openPersonnel.has(loc.participation_id);
          const isActive = loc.participation_status === "activated";
          const statusLine = formatStatusLine(
            loc.participation_status,
            isActive ? loc.activated_at : null,
          );
          return (
            <Box
              key={loc.participation_id}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, flexWrap: "wrap" }}>
                {/* Mirrors the cohort panel's chip pattern: identity in
                    the chip, status caption underneath, fixed minWidth
                    so all chips line up regardless of name length. */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.25 }}>
                  <Chip
                    size="small"
                    label={loc.name}
                    onDelete={() => onUnenroll(loc.location_id)}
                    sx={{
                      bgcolor: isActive ? brand.forest : brand.orange,
                      color: brand.pearl,
                      borderColor: isActive ? brand.forest : brand.orange,
                      minWidth: 320,
                      justifyContent: "space-between",
                      "& .MuiChip-deleteIcon": {
                        color: brand.pearl,
                        "&:hover": { color: brand.white },
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ pl: 1.5, fontStyle: "italic", display: "block" }}
                  >
                    Location ID: {loc.display_id}
                  </Typography>
                  {statusLine && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ pl: 1.5, fontStyle: "italic", display: "block" }}
                    >
                      {statusLine}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1 }} />
                <Tooltip title={open ? "Hide personnel" : "Show personnel"}>
                  <IconButton
                    size="small"
                    onClick={() => togglePersonnel(loc.participation_id)}
                    aria-label={open ? "Hide personnel" : "Show personnel"}
                  >
                    <People fontSize="small" sx={{ mr: 0.5 }} />
                    {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
              {open && (
                <Box sx={{ borderTop: 1, borderColor: "divider", px: 1, pb: 1 }}>
                  <LocationPersonnelTable
                    participationId={loc.participation_id}
                    enabled
                    locationId={loc.location_id}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>

      {eligible_locations.length > 0 && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderStyle: "dashed", borderColor: "divider", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary">
            {enrolled_locations.length === 0 ? "Enroll a location:" : "Enroll another location:"}
          </Typography>
          {eligible_locations.map((loc: PharmaEligibleLocation) => {
            const chip = (
              <Chip
                key={loc.location_id}
                size="small"
                variant="outlined"
                color={loc.master_in_phase_4 ? "primary" : "default"}
                label={`${loc.display_id} · ${loc.name}${loc.master_in_phase_4 ? "" : " · master not activated"}`}
                onClick={loc.master_in_phase_4 ? () => onEnroll(loc.location_id) : undefined}
                aria-disabled={!loc.master_in_phase_4 ? true : undefined}
                slotProps={!loc.master_in_phase_4 ? { label: { "aria-disabled": "true" } as React.HTMLAttributes<HTMLSpanElement> } : undefined}
                sx={!loc.master_in_phase_4 ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
              />
            );
            return loc.master_in_phase_4 ? chip : (
              <Tooltip key={loc.location_id} title={loc.blocked_reason ?? ""}>
                <span>{chip}</span>
              </Tooltip>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
